import React, {
  useCallback, useContext, useRef, useState,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';

import CoachSelect from '../../components/Leads/components/CoachSelect';
import PodContext from '../../../context/PodContext/PodContext';
import FirebaseContext from '../../../context/FirebaseContext';
import { useCoachConfig } from '../../hooks/useCoachConfig';
import CoachPod from '../../../Model/CoachPod';
import useToast from '../../hooks/useToast';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import { getPodClientCounts } from '../../utils/podCapacity';
import CoachHistory, { HistoryEventType } from '../../Model/CoachHistory';
import ConfirmDialog from '../../components/ConfirmDialog';
import CoachConfig from '../../Model/CoachConfig';
import {
  Container,
  HeaderContainer,
  DetailsContainer,
  HeaderTitle,
  LoadingContainer,
  ButtonContainer,
  HeaderDescription,
  UserCountDisplay,
} from './styles';

import texts from './texts.json';
import CoachPodTable from './components/CoachPodTable';
import CreateCoachPodModal from './components/CreateCoachPodModal/CreateCoachPodModal';

// eslint-disable-next-line max-len
const notionDocLink = 'https://www.notion.so/system2fitness/How-to-enable-pods-for-coaches-209fe9cbfa708099b099c2f64208963a';

const PodSettings = () => {
  const { fetchPodsForCoach, setAssistantCoachesAndAssignment, pods } = useContext(PodContext);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const {
    userDoc: {
      name: authUserName,
    },
    userId: authUserId,
  } = useContext(LoggedInUserContext);

  const [selectedCoach, setSelectedCoach] = useState({});
  const [loading, setLoading] = useState(false);
  const [isPodCreating, setIsPodCreating] = useState(false);
  const [isPodInitializing, setIsPodInitializing] = useState(false);
  const [isPodReBalancing, setIsPodReBalancing] = useState(false);
  const [createPodModalOpen, setCreatePodModalOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [podClientCounts, setPodClientCounts] = useState({});

  const { coachConfig } = useCoachConfig(selectedCoach.id);
  const { showToast } = useToast();

  const podClientCountsRequestRef = useRef(0);

  /*
    The pods collection is fetched with `mode: 'off'`, so nothing on this page updates by itself.
    Every action that moves clients between pods has to refresh these counts alongside the pods, or
    the table renders one pod's new membership against the other's stale numbers.
  */
  const refreshPodClientCounts = useCallback(async (coachId) => {
    podClientCountsRequestRef.current += 1;
    const requestId = podClientCountsRequestRef.current;

    const counts = await getPodClientCounts(coachId);

    /*
      The coach select stays enabled while this query runs, and it reads every in-pod client of the
      coach, so switching coach mid-flight is enough to have the slower request resolve last. Counts
      are keyed by pod id, so applying another coach's counts here would leave every visible pod
      defaulting to zero and reading as empty at 0% — the same wrong capacity, in the other
      direction. Only the newest request may write.
    */
    if (requestId !== podClientCountsRequestRef.current) {
      return;
    }

    setPodClientCounts(counts);
  }, []);

  const handleCoachChange = useCallback(async (coach) => {
    setLoading(true);
    setSelectedCoach(coach || {});

    if (coach?.id) {
      await Promise.all([
        fetchPodsForCoach(coach.id),
        setAssistantCoachesAndAssignment(coach.id),
        refreshPodClientCounts(coach.id),
      ]);
    }
    setLoading(false);
  }, [fetchPodsForCoach, setAssistantCoachesAndAssignment, refreshPodClientCounts]);

  const handlePodCreate = useCallback(async (name, maxUsers) => {
    try {
      setIsPodCreating(true);
      const podId = await CoachPod.create(selectedCoach.id, name, maxUsers);

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_CREATED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
        metadata: {
          podId,
          podName: name,
        },
      });

      showToast(texts.podCreated, { success: true });

      await fetchPodsForCoach(selectedCoach.id);
    } catch (error) {
      showToast(texts.podCreationError, { error: true });
    } finally {
      setIsPodCreating(false);
    }
  }, [authUserId, authUserName, fetchPodsForCoach, selectedCoach.id, showToast]);

  const handlePodInitialize = useCallback(async () => {
    try {
      setIsPodInitializing(true);

      const coachConfigDoc = await CoachConfig.getCoachConfigByCoachId(selectedCoach.id);

      if (coachConfigDoc) {
        await coachConfigDoc.enablePod();
      }

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_ENABLED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
      });

      showToast(texts.podEnabled, { success: true });
    } catch (error) {
      showToast(texts.podEnableError, { error: true });
    } finally {
      setIsPodInitializing(false);
    }
  }, [authUserId, authUserName, selectedCoach.id, showToast]);

  const handlePodReBalancing = useCallback(async () => {
    try {
      setShowConfirmationModal(false);
      setIsPodReBalancing(true);
      const response = await remote('coachPod-initializePods/rebalance', { coachId: selectedCoach.id });
      // Check if response failed
      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: Request failed`;
        throw new Error(errorMessage);
      }

      await CoachHistory.addCoachHistoryItem({
        type: HistoryEventType.COACH_POD_REBALANCED,
        coachId: selectedCoach.id,
        actionedBy: authUserId,
        actionedByUserName: authUserName,
      });

      showToast(texts.podReBalanced, { success: true });

      await Promise.all([fetchPodsForCoach(selectedCoach.id), refreshPodClientCounts(selectedCoach.id)]);
    } catch (error) {
      showToast(error.message || texts.podReBalancedError, { error: true });
    } finally {
      setIsPodReBalancing(false);
    }
  }, [authUserId, authUserName, fetchPodsForCoach, refreshPodClientCounts, remote, selectedCoach.id, showToast]);

  return (
    <Container>
      <HeaderContainer>
        <HeaderTitle>{texts.title}</HeaderTitle>
        <HeaderDescription>
          {texts.description}
          {' '}
          <a
            href={notionDocLink}
            target="_blank"
            rel="noreferrer"
          >
            {texts.howToUsePods}
          </a>
        </HeaderDescription>
      </HeaderContainer>
      <CoachSelect onChange={handleCoachChange} />
      <DetailsContainer>
        {loading && (
          <LoadingContainer>
            <CircularProgress size={20} color="info" />
          </LoadingContainer>
        )}
        {!loading && !selectedCoach.id && <div>{texts.selectCoach}</div>}
        {!loading && selectedCoach.id && (
          <>
            <ButtonContainer>
              {!coachConfig?.isPodEnabled && (
                <LoadingButton
                  onClick={handlePodInitialize}
                  loading={isPodInitializing}
                  disabled={isPodInitializing}
                  variant="contained"
                >
                  {texts.enablePods}
                </LoadingButton>
              )}
              {coachConfig?.isPodEnabled && (
                <>
                  <LoadingButton
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreatePodModalOpen(true)}
                    loading={isPodCreating}
                    disabled={isPodCreating}
                  >
                    {texts.createPod}
                  </LoadingButton>
                  <LoadingButton
                    variant="contained"
                    onClick={() => setShowConfirmationModal(true)}
                    loading={isPodReBalancing}
                    disabled={isPodReBalancing || pods.length <= 1}
                  >
                    {texts.rebalancePods}
                  </LoadingButton>
                  <UserCountDisplay>
                    {`${texts.totalActiveUsers} ${podClientCounts.activeTotal ?? 0}`}
                  </UserCountDisplay>
                  <UserCountDisplay>
                    {`${texts.totalAssignedUsers} ${podClientCounts.assignedTotal ?? 0}`}
                  </UserCountDisplay>
                </>
              )}
            </ButtonContainer>
            {coachConfig?.isPodEnabled && (
              <CoachPodTable
                selectedCoach={selectedCoach}
                podClientCounts={podClientCounts}
                refreshPodClientCounts={refreshPodClientCounts}
              />
            )}
          </>
        )}
      </DetailsContainer>
      <ConfirmDialog
        isOpen={showConfirmationModal}
        onConfirm={handlePodReBalancing}
        onCancel={() => setShowConfirmationModal(false)}
        dialogTexts={{
          title: texts.confirmationModal.title,
          content: texts.confirmationModal.description,
        }}
      />
      <CreateCoachPodModal
        open={createPodModalOpen}
        setOpen={setCreatePodModalOpen}
        loading={isPodCreating}
        handlePodCreate={handlePodCreate}
      />
    </Container>
  );
};

export default compose(
  observer,
)(PodSettings);
